"use client";

import { iResourcesBlockData } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { useState } from "react";

interface ResourcesSectionProps extends iResourcesBlockData {
  heading?: string;
  showResourceTitle?: boolean;
}

export function ResourcesSection({
  heading,
  videos = [],
  ebooks = [],
  showResourceTitle = false,
}: ResourcesSectionProps) {
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);

  if (videos.length === 0 && ebooks.length === 0) {
    return (
      <section className="bg-white rounded-lg shadow-md p-6 space-y-8">
        <div className="text-center text-gray-500">
          <p>No resources added</p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-lg shadow-md p-6 space-y-8">
      {heading && <h2 className="text-2xl font-semibold mb-6">{heading}</h2>}

      {/* Videos Section */}
      {videos.length > 0 && (
        <div className="space-y-4">
          <div className="@container">
            <div className={`grid ${
              videos.length === 1 ? 'grid-cols-1' : 'grid-cols-1 @[600px]:grid-cols-2 @[900px]:grid-cols-3'
            } gap-4`}>
              {videos.map((video) => (
                <div
                  key={video.id}
                  className="cursor-pointer group"
                  onClick={() => setSelectedVideo(video.videoId)}
                >
                  <div className="relative aspect-video">
                    <img
                      src={`https://img.youtube.com/vi/${video.videoId}/maxresdefault.jpg`}
                      alt={video.title}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-50 transition-all rounded-lg flex items-center justify-center">
                      <svg
                        className="w-12 h-12 text-white"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                  {showResourceTitle && (
                    <p className="mt-2 text-sm font-medium">{video.title}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* E-books Section */}
      {ebooks.length > 0 && (
        <div className="space-y-4">
          <div className="@container">
            <div className={`grid ${
              ebooks.length === 1 ? 'grid-cols-1' : 'grid-cols-1 @[600px]:grid-cols-2'
            } gap-4`}>
              {ebooks.map((ebook) => (
                <div key={ebook.id} className="w-full">
                  <iframe
                    className="w-full aspect-[3/4] rounded-lg border"
                    src={ebook.url}
                    title={ebook.title}
                    allowFullScreen
                  ></iframe>
                  {showResourceTitle && (
                    <p className="mt-2 text-sm font-medium">{ebook.title}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Video Lightbox */}
      {selectedVideo && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4 -top-10"
          onClick={() => setSelectedVideo(null)}
        >
          <div className="relative w-full max-w-4xl aspect-video">
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <iframe
              className="w-full h-full rounded-lg"
              src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1`}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}
    </section>
  );
}
